import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../environments/environment';

const APP_TITLE = environment.titleAppName;

const TITLE_BY_ROUTE: Array<{ title: string; regex: RegExp }> = [
  { title: 'i18n.title-tab.login', regex: /^\/login/ },
  { title: 'i18n.title-tab.labo', regex: /^\/labo/ },
  { title: 'i18n.title-tab.reset-request', regex: /^\/reset-request/ },
  { title: 'i18n.title-tab.new-password', regex: /^\/new-password/ },
  { title: 'i18n.title-tab.not-found', regex: /^\/not-found/ },
  { title: 'i18n.title-tab.maintenance', regex: /^\/maintenance/ },
  { title: 'i18n.title-tab.dashboard', regex: /^\/dashboard/ },
  { title: 'i18n.title-tab.sav-logbook', regex: /^\/logbook/ },
  { title: 'i18n.title-tab.profil-pti', regex: /^\/profil-pti/ },
  { title: 'i18n.title-tab.alarms', regex: /^\/alarms/ },
  { title: 'i18n.title-tab.daily-report-act', regex: /^\/daily-report-act/ },
  { title: 'i18n.title-tab.report-onss', regex: /^\/report-onss/ },
  { title: 'i18n.title-tab.ent-params', regex: /^\/ent-params/ },
  { title: 'i18n.title-tab.chapier-tour', regex: /^\/chapier\/tour/ },
  {
    title: 'i18n.title-tab.daily-report-act-admin',
    regex: /^\/daily-report-act-admin/,
  },
  {
    title: 'i18n.title-tab.infodata-admin-menu',
    regex: /^\/infodata-admin-menu/,
  },
];

@Injectable({
  providedIn: 'root',
})
export class TitleTabService {
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  private lastUrlChecked = '';

  private _firstLevel = APP_TITLE;
  public get firstLevel(): string {
    return this._firstLevel;
  }
  public set firstLevel(value: string) {
    this._firstLevel = value;
    this.updateTitle();
  }

  private _secondLevel = '';
  public get secondLevel(): string {
    return this._secondLevel;
  }
  public set secondLevel(value: string) {
    this._secondLevel = value;
    this.updateTitle();
  }

  private _thirdLevel = '';
  public get thirdLevel(): string {
    return this._thirdLevel;
  }
  public set thirdLevel(value: string) {
    this._thirdLevel = value;
    this.updateTitle();
  }

  constructor() {
    this.translateService.onLangChange.pipe(takeUntilDestroyed()).subscribe({
      next: () => {
        this.updateTitle();
      },
    });
    this.translateService.onTranslationChange
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: () => {
          this.updateTitle();
        },
      });
  }

  public init(): void {
    this.router.events.subscribe({
      next: (event) => {
        if (event instanceof NavigationEnd) {
          const urlToCheck = event.urlAfterRedirects.split('?').at(0);
          if (urlToCheck === this.lastUrlChecked) {
            return;
          }
          this.lastUrlChecked = urlToCheck;
          this.reset();
          const matchedRoute = TITLE_BY_ROUTE.find((r) =>
            r.regex.test(event.urlAfterRedirects),
          );
          if (matchedRoute) {
            this.secondLevel = matchedRoute.title;
          }
          this.updateTitle();
        }
      },
    });
  }

  public reset(): void {
    this._secondLevel = '';
    this._thirdLevel = '';
    this.firstLevel = APP_TITLE;
  }

  public updateTitle(): void {
    let title = this.firstLevel;
    if (this.secondLevel) {
      title += ` | ${this.translateService.instant(this.secondLevel)}`;
    }
    if (this.thirdLevel) {
      title += ` | ${this.translateService.instant(this.thirdLevel)}`;
    }
    document.title = title;
  }
}
